/**
 * The home dashboard's data: `GET /api/stats`, loaded once, refreshed on request.
 *
 * **No polling.** These are full-corpus aggregates that move when a human draws a box, not on a
 * timer. A dashboard that re-runs them every few seconds burns the service's throughput on a
 * page that is usually just sitting open, and it makes the numbers feel live when the honest
 * description is "as of the moment you asked" — which is why `loadedAt` is surfaced and shown.
 *
 * A failed load is a **state**, not a toast: `problem` stays set until a refresh succeeds. The
 * page must never fall back to rendering zeros, because zero is a legitimate value here (there
 * are genuinely no boxes yet) and "the API is down" and "nothing annotated yet" would then look
 * identical.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import type { StatsResponse } from '../api/types';

class StatsState {
  data = $state<StatsResponse | null>(null);
  problem = $state<ApiProblem | null>(null);
  loading = $state(false);
  loadedAt = $state<Date | null>(null);

  /** Load unless something is already here. Safe to call from a mount effect. */
  async ensure(): Promise<void> {
    if (this.data || this.loading) return;
    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      this.data = await api.stats();
      this.problem = null;
      this.loadedAt = new Date();
    } catch (error) {
      // The previous payload is deliberately kept: stale-but-labelled beats blank, and the
      // banner says which it is.
      this.problem = asProblem(error);
    } finally {
      this.loading = false;
    }
  }

  /** For tests, which must not inherit another test's payload. */
  reset(): void {
    this.data = null;
    this.problem = null;
    this.loading = false;
    this.loadedAt = null;
  }
}

export const stats = new StatsState();
